import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import stripePlatform from '../lib/stripe-platform';
import { stripeConfig } from '../lib/config';

interface Subscription {
  id: string;
  status: string;
  plan_id: string;
  current_period_end: string;
  stripe_customer_id: string;
}

const Billing: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const returnPath = (location.state as any)?.from || '/';

  useEffect(() => {
    async function loadSubscriptionData() {
      try {
        // Check if supabase is initialized
        if (!supabase) {
          console.error('Supabase client is not initialized');
          setIsLoading(false);
          return;
        }
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setIsLoading(false);
          return;
        }

        // Fetch subscription data
        const { data: subData, error: subError } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .single();
          
        if (subError && subError.code !== 'PGRST116') {
          console.error('Error fetching subscription:', subError);
        }
        
        if (subData) {
          setSubscription(subData as Subscription);
          
          // If we have a subscription, fetch payment methods
          try {
            const methods = await stripePlatform.getPaymentMethods(subData.stripe_customer_id);
            setPaymentMethods(methods);
          } catch (error) {
            console.error('Error fetching payment methods:', error);
          }
        }
      } catch (error) {
        console.error('Error loading subscription data:', error);
      } finally {
        setIsLoading(false);
      }
    }
    
    loadSubscriptionData();
  }, []);

  // Start a new subscription
  const handleStartSubscription = async () => {
    // Navigate to Terms of Service page
    navigate('/terms-of-service', { state: { from: location.pathname } });
  };
  
  // Open customer portal to manage subscription
  const handleManageSubscription = async () => {
    if (!subscription) return;
    
    try {
      setIsCreatingSession(true);
      
      // Create a billing portal session
      const session = await stripePlatform.createBillingPortalSession(
        subscription.stripe_customer_id,
        `${window.location.origin}/billing`
      );
      
      // Redirect to portal
      if (session.url) {
        window.location.href = session.url;
      }
    } catch (error) {
      console.error('Error creating billing portal session:', error);
      alert('Failed to open billing portal. Please try again.');
    } finally {
      setIsCreatingSession(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">Loading subscription data...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="text-center">
        <h1 className="text-3xl font-extrabold text-gray-900">Subscription Management</h1>
        <p className="mt-4 text-lg text-gray-600">
          Manage your subscription and billing information
        </p>
      </div>

      <div className="mt-12 bg-white shadow overflow-hidden rounded-lg">
        {subscription ? (
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-lg leading-6 font-medium text-gray-900">Current Subscription</h2>
            
            <div className="mt-5 border-t border-gray-200 pt-5">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      subscription.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : subscription.status === 'trialing' 
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                    </span>
                  </dd>
                </div>
                
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Plan</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {subscription.plan_id === stripePlatform.SUBSCRIPTION_PLANS.STANDARD 
                      ? 'Essential Plan' 
                      : subscription.plan_id}
                  </dd>
                </div>
                
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Current Period Ends</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(subscription.current_period_end).toLocaleDateString()}
                  </dd>
                </div>
                
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Payment Method</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {paymentMethods.length > 0 ? (
                      <div className="flex items-center">
                        <span className="mr-2">
                          {paymentMethods[0].card.brand.charAt(0).toUpperCase() + 
                           paymentMethods[0].card.brand.slice(1)}
                        </span>
                        <span>•••• {paymentMethods[0].card.last4}</span>
                      </div>
                    ) : (
                      'No payment method found'
                    )}
                  </dd>
                </div>
              </dl>
            </div>
            
            <div className="mt-8">
              <button
                type="button"
                onClick={handleManageSubscription}
                disabled={isCreatingSession}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mr-3"
              >
                {isCreatingSession ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : 'Manage Subscription'}
              </button>
              
              <button
                type="button"
                onClick={() => navigate('/')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Go to Home
              </button>
            </div>
          </div>
        ) : (
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-lg leading-6 font-medium text-gray-900">No Active Subscription</h2>
            <div className="mt-2 max-w-xl text-sm text-gray-500">
              <p>
                You don't have an active subscription. Subscribe now to access all features.
              </p>
            </div>
            <div className="mt-5">
              <button
                type="button"
                onClick={handleStartSubscription}
                disabled={isCreatingSession}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mr-3"
              >
                {isCreatingSession ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : 'Subscribe Now'}
              </button>
              
              <button
                type="button"
                onClick={async () => {
                  if (supabase) {
                    await supabase.auth.signOut();
                  }
                  navigate('/login');
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Return to Login
              </button>
            </div>
          </div>
        )}
      </div>
      
      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={() => navigate(returnPath)}
          className="text-sm font-medium text-blue-600 hover:text-blue-500"
        >
          Return to previous page
        </button>
      </div>
    </div>
  );
};

export default Billing; 